import {ChangeDetectionStrategy, Component, computed, DestroyRef, Inject, Injectable, signal} from "@angular/core";
import {BankIdSigneringStatus, DokumentId, DokumentSignatarModell, DokumentsigneringRessursClient, DokumentVersjonModell, PotensiellDokumentSignatarModell, Signeringsmetode, TofaktorSigneringskodeKanal} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {DokumentSigneringInformasjon, SigneringsmetodeModell} from "felles-ng2/tjenester/DokumentSigneringInformasjon";
import {erIkkeTomListe, første} from "felles-ng2/støttefunksjoner/lister";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {filter, finalize, firstValueFrom, tap} from "rxjs";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {i18n, I18nTekst} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatToolbar} from "@angular/material/toolbar";
import {TranslateModule} from "@ngx-translate/core";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {DokumentVisPdfIframeKomponent} from "../../../dokument/komponenter/dokument-vis-pdf-iframe/dokument-vis-pdf-iframe";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatOptgroup, MatOption, MatSelect, MatSelectTrigger} from "@angular/material/select";
import {NgOptimizedImage, NgTemplateOutlet} from "@angular/common";
import {MatProgressBar} from "@angular/material/progress-bar";
import {SigneringCriiptoKomponent} from "../../komponenter/signering-criipto/signering-criipto";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {tomDokumentVersjonModell} from "felles-ng2/modell/tommeModeller";
import {SigneringOtpDialogKomponent} from "web-ng2/moduler/signering/modale/signering-otp-dialog/signering-otp-dialog";
import {BANKID_TERMINERENDE_STATUSER, BankidKlientFeil, BankidKlientFeilError} from "web-ng2/moduler/signering/tjenester/signering-bankid-qes-service";
import {SigneringBankidStatus} from "web-ng2/moduler/signering/komponenter/signering-bankid-status/signering-bankid-status";
import {DokumentSigneringDatatjeneste} from "felles-ng2/tjenester/datalager/DokumentSigneringDatatjeneste";
import {ajs_ModelloppdateringTjeneste_DI_TOKEN, AjsModelloppdateringTjeneste} from "felles-ng2/tjenester/ajs/AjsModelloppdateringTjeneste";
import {ajs_$log_DI_TOKEN, AjsLogService} from "web-ng2/tjenester/ajs/AjsLogService";
import {PotensieltPromise, somPromise} from "felles-ng2/støttefunksjoner/promiser";


const debug = initDebugger('SigneringSignerDialogKomponent');

/**
 * Dialog for signering av dokument
 */
@Component({
    selector: 'signering-signer-dialog',
    templateUrl: 'signering-signer-dialog.html',
    styleUrl: 'signering-signer-dialog.scss',
    host: {'data-testid': 'signering-signer-dialog'},
    imports: [
        FormsModule,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatIcon,
        MatIconButton,
        MatToolbar,
        ReactiveFormsModule,
        TranslateModule,
        VisSpinnerDirektiv,
        DokumentVisPdfIframeKomponent,
        MatSlideToggle,
        MatSelect,
        MatOptgroup,
        MatOption,
        NgOptimizedImage,
        MatSelectTrigger,
        MatProgressBar,
        NgTemplateOutlet,
        SigneringCriiptoKomponent,
        SigneringBankidStatus
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SigneringSignerDialogKomponent {

    static vis(dialog: MatDialog, dokumentversjonPromise: PotensieltPromise<DokumentVersjonModell>, signatarInformasjonPromise: PotensieltPromise<SignatarInformasjon>): MatDialogRef<SigneringSignerDialogKomponent, SigneringSignerDialogRespons> {
        return dialog.open(SigneringSignerDialogKomponent, {
            data: {dokumentversjon: somPromise(dokumentversjonPromise), signatarInformasjon: somPromise(signatarInformasjonPromise)} as DialogParametre,
            disableClose: true,
            ariaLabelledBy: 'signering-signer-dialog-tittel',
            height: '80%',
            minWidth: '700px', //steg-raden er ca 670px min bredde
            maxWidth: '1000px',
            width: '80vw'
        });
    }

    dokumentversjon = signal<DokumentVersjonModell>(tomDokumentVersjonModell());
    /** Sann når dokumentversjon-promiset angitt via {@link DialogParametre} har resolvet */
    dokumentversjonLastet = signal(false);
    signatar = signal<DokumentSignatarModell | null>(null);
    aksepterteSignaturmetodeModeller = computed(() =>
      this.dokumentSigneringInformasjon.aksepterteSignaturmetoderSomModeller(this.dokumentversjon().aksepterteSignaturmetoder))
    modus = signal<'lesDokument' | 'bankidSignering' | 'signerDialog'>('lesDokument');
    laster = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());
    signeringStatus = signal<BankIdSigneringStatus | BankidKlientFeil | null>(null);

    skjema = new FormGroup({
        dokumentLest: new FormControl(false, {nonNullable: true, validators: Validators.requiredTrue}),
        valgtSigneringsmetode: new FormControl<SigneringsmetodeModell | null>(null, {nonNullable: true, validators: Validators.required})
    })

    private markertSomLest = false;

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                @Inject(DestroyRef) private destroyRef: DestroyRef,
                private dialogRef: MatDialogRef<SigneringSignerDialogKomponent, SigneringSignerDialogRespons>,
                private avhengigheter: SigneringSignerDialogKomponentAvhengigheter,
                private dokumentSigneringInformasjon: DokumentSigneringInformasjon,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private matDialog: MatDialog) {
        this.initData(parametre.dokumentversjon, parametre.signatarInformasjon);

        this.skjema.controls.dokumentLest.valueChanges
          .pipe(takeUntilDestroyed(), filter(lest => lest && !this.markertSomLest))
          .subscribe(() => this.markerSomLest());

        // Setter modus = bankid-signering for å informere bruker om at signering åpner nytt vindu
        this.skjema.controls.valgtSigneringsmetode.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(metode => {
              if(metode != null && metode.metode === Signeringsmetode.norsk_bankid_netsentrisk_pdf) {
                  this.modus.set('bankidSignering')
              } else {
                  this.modus.set("lesDokument")
              }
          });
    }

    signer() {
        if (this.skjema.value.valgtSigneringsmetode!.metode == Signeringsmetode.lederplan_autentisert_tofakt_sms) {
            this.signerEnkeltElektroniskOtpPerSms();
        } else if (this.skjema.value.valgtSigneringsmetode!.metode == Signeringsmetode.lederplan_autentisert_tofakt_epost) {
            this.signerEnkeltElektroniskOtpPerEpost();
        } else if (this.skjema.value.valgtSigneringsmetode!.metode === Signeringsmetode.norsk_bankid_netsentrisk_pdf) {
            this.signerBankIDQES();
        } else {
            this.gåTilVisSignerdialog();
        }
    }

    signert() {
        this.dialogRef.close(true);
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    //private hjelpemetoder

    private async initData(dokumentversjonPromise: Promise<DokumentVersjonModell>, signatarInformasjonPromise: Promise<SignatarInformasjon>) {
        this.dokumentversjon.set(await dokumentversjonPromise);
        this.dokumentversjonLastet.set(true);

        const signatarInformasjon = await signatarInformasjonPromise;
        this.signatar.set(signatarInformasjon.signatar);

        //serversiden garanterer at antattStoettedeSigneringsmetoder er et subsett av dokumentversjon.aksepterteSignaturmetoder
        if (erIkkeTomListe(signatarInformasjon.antattStoettedeSigneringsmetoder)) {
            const antattStøttetSigneringsmetode = første(signatarInformasjon.antattStoettedeSigneringsmetoder);
            if (erIkkeNil(antattStøttetSigneringsmetode))
                this.skjema.controls.valgtSigneringsmetode.setValue(this.dokumentSigneringInformasjon.signeringsmetodeSomModell(antattStøttetSigneringsmetode))
        }
    }

    private async markerSomLest() {
        const arbeidPågår = this.laster.start();
        try {
            this.markertSomLest = true;
            await this.avhengigheter.markerSomLest(this.dokumentversjon().dokumentId);
        } catch (error) {
            this.markertSomLest = false;
            this.meldingTilBruker.feilmelding(new I18nTekst(i18n('En feil oppstod under markering av dokumentet som lest')))
        } finally {
            arbeidPågår.avsluttet();
        }
    }

    private async signerEnkeltElektroniskOtpPerSms() {
        const signert = await firstValueFrom(SigneringOtpDialogKomponent.vis(this.matDialog, this.dokumentversjon(), TofaktorSigneringskodeKanal.sms).afterClosed());
        if (signert) this.signert();
        else this.avbryt();
    }

    private async signerEnkeltElektroniskOtpPerEpost() {
        const signert = await firstValueFrom(SigneringOtpDialogKomponent.vis(this.matDialog, this.dokumentversjon(), TofaktorSigneringskodeKanal.epost).afterClosed());
        if (signert) this.signert();
        else this.avbryt();
    }

    private async signerBankIDQES() {
        const arbeidPågår = this.lagrer.start();
        // show how to signal to observable
        this.modus.set('bankidSignering');
        try {
            const dokumentVersjon = this.dokumentversjon();

            this.avhengigheter.signerMedBankid(dokumentVersjon, this.signatar()!)
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => arbeidPågår.avsluttet())
              ).subscribe({
                next: status => {
                    this.signeringStatus.set(status);

                    if (status === BankIdSigneringStatus.avvist) return this.meldingTilBruker.advarselmelding(new I18nTekst(i18n('Signeringen ble avvist. Du må godta å dele personopplysninger.'))); // Bruker har ikke samtykket til å dele personopplysingene sine i bankid-flyten
                    if (status === BankIdSigneringStatus.feilet) return this.meldingTilBruker.feilmelding(new I18nTekst(i18n('En feil oppstod under signering med BankID.'))); // Bruker har f.eks. ikke logget på med bankid eller har avbrutt prosessen -etter- at hen klikket Signer i bankid-flyten
                    if (status === BankIdSigneringStatus.utlopt) return this.meldingTilBruker.feilmelding(new I18nTekst(i18n('Signeringen utløp på tid..'))); // Timeout som oppstår når brukeren ikke har startet signering (klikket Signer) i bankid-flyten før timeout er nådd. Se BankIDOpenIdSigneringKonfigurasjon for verdi i sekunder.
                    // Hvis status er signering_fullfort viser vi status-komponent + knapp for å lukke, bruker får se bekreftelse på at de har signert.
                },
                error: e => {
                    debug('Feil ved oppstart av BankID-signering', e);
                    this.avhengigheter.loggFeil("bankid_signering_klientfeil") // Vent med denne, hvordan logge riktig ift å ikke logge token osv?
                    if(e instanceof BankidKlientFeilError) {
                        this.signeringStatus.set(e.klientFeil);
                        this.meldingTilBruker.feilmelding(e.brukerrettetMelding);
                        return;
                    }

                    debug('Feil ved BankID-signering', e);
                    this.signeringStatus.set(BankIdSigneringStatus.feilet);
                    this.meldingTilBruker.feilmelding(new I18nTekst(i18n('En feil oppstod med signeringsløsningen. Forsøk på nytt om litt.')));
                },
            });
        } catch (e) {
            debug('Feil ved oppstart av BankID-signering', e);
            this.signeringStatus.set(BankIdSigneringStatus.feilet);
            this.meldingTilBruker.feilmelding(new I18nTekst(i18n('En feil oppstod under kommunikasjon med signeringsløsningen. Forsøk på nytt om litt.')));
        }
    }

    private gåTilVisSignerdialog() {
        this.modus.set('signerDialog');
    }

    protected readonly BankIdSigneringStatus = BankIdSigneringStatus;
}

export type SigneringSignerDialogRespons = boolean;

export type DialogParametre = {
    dokumentversjon: Promise<DokumentVersjonModell>;
    signatarInformasjon: Promise<SignatarInformasjon>
};

export type SignatarInformasjon = Omit<PotensiellDokumentSignatarModell, 'signatarFunnet'>;

@Injectable()
export class SigneringSignerDialogKomponentAvhengigheter {

    constructor(private dokumentsigneringRessurs: DokumentsigneringRessursClient,
                private dokumentSigneringDatatjeneste:DokumentSigneringDatatjeneste,
                @Inject(ajs_ModelloppdateringTjeneste_DI_TOKEN) private modelloppdateringTjeneste: AjsModelloppdateringTjeneste,
                @Inject(ajs_$log_DI_TOKEN) private $log: AjsLogService,) {
    }

    loggFeil(feiltype:string) {
        this.$log.error(feiltype);
    }

    async markerSomLest(dokumentId: DokumentId) {
        await this.dokumentsigneringRessurs.markerSomLest(dokumentId).somPromise();
    }

    signerMedBankid(dokument:DokumentVersjonModell, signtar:DokumentSignatarModell) {
        return this.dokumentSigneringDatatjeneste.signerMedBankID(dokument, signtar).pipe(tap( status => {
            if(BANKID_TERMINERENDE_STATUSER.has(status)) {
                this.kringkastSigneringLokalt(dokument.dokumentId);
            }
        }));
    }

    private kringkastSigneringLokalt(dokumentId:DokumentId) {
        return this.modelloppdateringTjeneste.broadcastLokalSignatarSignert(dokumentId);
    }
}
